import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../common/modal';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import { useDomains } from '../../hooks/useClients';
import type { ResourcesFilterValues } from '../../containers/content-library/hooks/useContentLibraryUrlState';
import { TagGroupMultiSelect, DomainSingleSelect, ClientSingleSelectWithSearch } from './filter-controls';

export type { ResourcesFilterValues };

interface ResourcesFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (values: ResourcesFilterValues) => void;
  initialValues: ResourcesFilterValues;
}

const STATUS_OPTIONS: { value: 'live' | 'hidden'; label: string }[] = [
  { value: 'live', label: 'Live' },
  { value: 'hidden', label: 'Hidden' },
];

const ResourcesFilterModal: React.FC<ResourcesFilterModalProps> = ({ isOpen, onClose, onApply, initialValues }) => {
  const [values, setValues] = useState<ResourcesFilterValues>(initialValues);

  const { data: tagGroupsData, isLoading: isLoadingTagGroups } = use2026TagGroups();
  const topicTagGroups = useMemo(
    () => (tagGroupsData ?? []).filter((g) => g.tag_group_type === 'topic'),
    [tagGroupsData],
  );
  const { data: domains } = useDomains();
  const domainOptions = useMemo(() => (domains ?? []).map((d) => ({ id: d.id, name: d.name })), [domains]);

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues);
    }
  }, [isOpen, initialValues]);

  const update = useCallback(<K extends keyof ResourcesFilterValues>(key: K, v: ResourcesFilterValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: v }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(values);
    onClose();
  }, [values, onApply, onClose]);

  const handleClear = useCallback(() => {
    setValues({
      tagGroupIds: [],
      tagIds: [],
      domainId: null,
      clientId: null,
      statuses: [],
    });
  }, []);

  const hasFilters = useMemo(
    () =>
      values.tagGroupIds.length > 0 ||
      values.tagIds.length > 0 ||
      values.domainId != null ||
      values.clientId != null ||
      values.statuses.length > 0,
    [values],
  );

  if (!isOpen) return null;

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          className="inline-block w-full max-w-md transform overflow-visible rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:align-middle"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Filter Resources</h3>
              <button
                type="button"
                className="text-blue-primary text-sm font-semibold hover:underline"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
          <div className="space-y-4 px-6 py-5">
            <div>
              <TagGroupMultiSelect
                label="Topic"
                tagGroups={topicTagGroups}
                selectedIds={values.tagGroupIds}
                onChange={(ids) => update('tagGroupIds', ids)}
                placeholder="Select topics"
                isLoading={isLoadingTagGroups}
              />
              <p className="mt-1 text-xs text-gray-500">Filter by topics associated with the resource.</p>
            </div>
            <div>
              <DomainSingleSelect
                label="Enabled for domain"
                domains={domainOptions}
                selectedId={values.domainId}
                onChange={(id) => update('domainId', id)}
                placeholder="All domains"
              />
              <p className="mt-1 text-xs text-gray-500">
                Shows resources that at least one client in this domain can use.
              </p>
            </div>
            <div>
              <ClientSingleSelectWithSearch
                label="Client"
                selectedClientId={values.clientId}
                onChange={(id) => update('clientId', id)}
                placeholder="All clients"
              />
              <p className="mt-1 text-xs text-gray-500">Filter by client associated with the resource.</p>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((opt) => {
                  const isChecked = values.statuses.includes(opt.value);
                  return (
                    <label
                      key={opt.value}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-700"
                    >
                      <input
                        type="checkbox"
                        className="text-blue-primary focus:ring-blue-primary h-4 w-4 rounded border-gray-300"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            update(
                              'statuses',
                              values.statuses.filter((s) => s !== opt.value),
                            );
                          } else {
                            update('statuses', [...values.statuses, opt.value]);
                          }
                        }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">Include Live and/or Hidden resources.</p>
            </div>
          </div>
          <div className="flex justify-between gap-2 border-t border-gray-100 px-6 py-4">
            <button
              type="button"
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              onClick={handleClear}
              disabled={!hasFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              onClick={handleApply}
            >
              Apply Filters
            </button>
          </div>
        </div>
      }
    />
  );
};

export default ResourcesFilterModal;
